import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProfileFormData } from '@/components/profile/ProfileForm';
import { toast } from '@/components/ui/Toast';
import { useTenant } from '@/contexts/TenantContext';

// Setup wizard progress type
export interface SetupWizardProgress {
  currentStep?: number;
  completedSteps?: string[];
  skippedAt?: string | null;
  entityCreated?: boolean;
  firstEntityId?: string | null;
}

// Extended profile type including avatar and setup wizard
export interface UserProfileExtended {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  companyName?: string;
  bio?: string;
  timezone?: string;
  language?: string;
  avatarUrl?: string;
  hasCompletedSetup: boolean;
  setupCompletedAt?: string | null;
  setupWizardProgress?: SetupWizardProgress;
  createdAt: string;
  updatedAt: string;
}

/** Sentinel UUID used when no real tenant is known — never send to backend */
const SENTINEL_TENANT_ID = '00000000-0000-0000-0000-000000000000';

/** Build tenant headers for API requests (excludes sentinel UUID) */
function tenantHeaders(tenantId?: string): Record<string, string> {
  if (!tenantId || tenantId === SENTINEL_TENANT_ID) return {};
  return { 'X-Tenant-ID': tenantId };
}

// Profile API - Worker API integration
const profileApi = {
  /**
   * Fetch user profile from Worker API
   */
  async fetchProfile(tenantId?: string): Promise<UserProfileExtended> {
    const response = await fetch('/api/profile', {
      credentials: 'include',
      headers: tenantHeaders(tenantId),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }

    const data = (await response.json()) as { profile: UserProfileExtended };
    return data.profile;
  },

  /**
   * Update user profile via Worker API
   */
  async updateProfile(data: ProfileFormData, tenantId?: string): Promise<UserProfileExtended> {
    const response = await fetch('/api/profile', {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...tenantHeaders(tenantId) },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update profile');
    }

    const result = (await response.json()) as { profile: UserProfileExtended };
    return result.profile;
  },

  /**
   * Upload avatar to Worker API (R2 storage)
   */
  async uploadAvatar(file: File, blob: Blob, tenantId?: string): Promise<string> {
    const formData = new FormData();
    formData.append('avatar', blob, file.name);

    const response = await fetch('/api/profile/avatar', {
      method: 'POST',
      credentials: 'include',
      headers: tenantHeaders(tenantId),
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload avatar');
    }

    const data = (await response.json()) as { avatarUrl: string };
    return data.avatarUrl;
  },

  /**
   * Delete avatar via Worker API
   */
  async deleteAvatar(tenantId?: string): Promise<void> {
    const response = await fetch('/api/profile/avatar', {
      method: 'DELETE',
      credentials: 'include',
      headers: tenantHeaders(tenantId),
    });

    if (!response.ok) {
      throw new Error('Failed to delete avatar');
    }
  },
};

/**
 * Custom hook for profile management
 * Uses Worker API for all operations (authenticated via session cookie)
 */
export function useProfile(userId: string) {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();

  // Fetch profile from Worker API
  const {
    data: profile,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['profile', userId, tenant?.id],
    queryFn: () => profileApi.fetchProfile(tenant?.id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userId, // Only fetch if userId is provided
  });

  // Update avatar URL when profile changes
  useEffect(() => {
    if (profile?.avatarUrl) {
      setAvatarUrl(profile.avatarUrl);
    } else {
      setAvatarUrl(undefined);
    }
  }, [profile]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: ProfileFormData) => profileApi.updateProfile(data, tenant?.id),
    onSuccess: (data) => {
      queryClient.setQueryData(['profile', userId], data);
      toast.success('Profile updated', {
        description: 'Your profile has been updated successfully.',
      });
    },
    onError: (error) => {
      console.error('Profile update error:', error);
      toast.error('Update failed', {
        description: 'Failed to update your profile. Please try again.',
      });
    },
  });

  // Upload avatar mutation
  const uploadAvatarMutation = useMutation({
    mutationFn: ({ file, blob }: { file: File; blob: Blob }) =>
      profileApi.uploadAvatar(file, blob, tenant?.id),
    onSuccess: (url) => {
      setAvatarUrl(url);
      // Update profile query data
      queryClient.setQueryData(['profile', userId], (old: UserProfileExtended | undefined) => {
        if (!old) return old;
        return { ...old, avatarUrl: url, updatedAt: new Date().toISOString() };
      });
    },
    onError: (error) => {
      console.error('Avatar upload error:', error);
      throw error; // Re-throw for component handling
    },
  });

  // Delete avatar mutation
  const deleteAvatarMutation = useMutation({
    mutationFn: () => profileApi.deleteAvatar(tenant?.id),
    onSuccess: () => {
      setAvatarUrl(undefined);
      // Update profile query data
      queryClient.setQueryData(['profile', userId], (old: UserProfileExtended | undefined) => {
        if (!old) return old;
        return { ...old, avatarUrl: undefined, updatedAt: new Date().toISOString() };
      });
    },
    onError: (error) => {
      console.error('Avatar delete error:', error);
      throw error; // Re-throw for component handling
    },
  });

  return {
    // Profile data
    profile,
    isLoading,
    error,

    // Avatar
    avatarUrl,

    // Mutations
    updateProfile: updateProfileMutation.mutateAsync,
    uploadAvatar: async (file: File, blob: Blob) => {
      await uploadAvatarMutation.mutateAsync({ file, blob });
    },
    deleteAvatar: async () => {
      await deleteAvatarMutation.mutateAsync();
    },

    // Loading states
    isUpdating: updateProfileMutation.isPending,
    isUploadingAvatar: uploadAvatarMutation.isPending,
    isDeletingAvatar: deleteAvatarMutation.isPending,

    // Utility
    refetch,
  };
}

/**
 * Get full name from profile
 */
export function getFullName(profile: UserProfileExtended | undefined): string {
  if (!profile) return '';
  return `${profile.firstName} ${profile.lastName}`.trim();
}

/**
 * Get display name (prefers displayName, falls back to full name)
 */
export function getDisplayName(profile: UserProfileExtended | undefined): string {
  if (!profile) return '';
  return profile.displayName || getFullName(profile);
}
